import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
// Fundamentals
import PoolSystem from "./pages/wiki/fundamentals/PoolSystem";
import Chemistry from "./pages/wiki/fundamentals/Chemistry";
import Circulation from "./pages/wiki/fundamentals/Circulation";
import Cleaning from "./pages/wiki/fundamentals/Cleaning";
// Equipment
import Filters from "./pages/wiki/equipment/Filters";
import SaltSystems from "./pages/wiki/equipment/SaltSystems";
import Pumps from "./pages/wiki/equipment/Pumps";
import Actuators from "./pages/wiki/equipment/Actuators";
// Water Conditions
import Algae from "./pages/wiki/conditions/Algae";
import CloudyWater from "./pages/wiki/conditions/CloudyWater";
import GreenToClean from "./pages/wiki/conditions/GreenToClean";
// Field Procedures
import ServiceWorkflow from "./pages/wiki/procedures/ServiceWorkflow";
import EquipmentSOPs from "./pages/wiki/procedures/EquipmentSOPs";
import Emergency from "./pages/wiki/procedures/Emergency";
// Onboarding
import OnboardingWelcome from "./pages/wiki/onboarding/Welcome";
import FirstDay from "./pages/wiki/onboarding/FirstDay";
import Training from "./pages/wiki/onboarding/Training";
// Service
import Standards from "./pages/wiki/service/Standards";
import AppDocumentation from "./pages/wiki/service/AppDocumentation";
import CustomerInteraction from "./pages/wiki/service/CustomerInteraction";
import Safety from "./pages/wiki/service/Safety";
// Compensation
import PayIncentives from "./pages/wiki/compensation/PayIncentives";
import Calculator from "./pages/wiki/compensation/Calculator";
import CompensationFAQ from "./pages/wiki/compensation/FAQ";
// Policies
import ICStatus from "./pages/wiki/policies/ICStatus";
import Responsibilities from "./pages/wiki/policies/Responsibilities";
import TimeOffTermination from "./pages/wiki/policies/TimeOffTermination";
import Uniform from "./pages/wiki/policies/Uniform";
import Conduct from "./pages/wiki/policies/Conduct";
// Resources
import RouteCoverage from "./pages/wiki/resources/RouteCoverage";
import Chemicals from "./pages/wiki/resources/Chemicals";
import EmergencyContacts from "./pages/wiki/resources/Contacts";
import Forms from "./pages/wiki/resources/Forms";
import ResourceFAQ from "./pages/wiki/resources/FAQ";
// Profile
import ProfileSettings from "./pages/profile/Settings";
import ProfileDashboard from "./pages/profile/Dashboard";
// Admin
import AdminUsers from "./pages/admin/Users";
import AdminActivityLogs from "./pages/admin/ActivityLogs";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import Verify from "./pages/auth/Verify";
import ResetPassword from "./pages/auth/ResetPassword";
import Unauthorized from "./pages/auth/Unauthorized";
import VerifyRequired from "./pages/auth/VerifyRequired";
import Logout from "./pages/auth/Logout";

const App = () => (
  <TooltipProvider>
    <Sonner />
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          {/* Auth routes - public */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/signup" element={<Signup />} />
          <Route path="/auth/verify" element={<Verify />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/logout" element={<Logout />} />
          <Route path="/auth/unauthorized" element={<Unauthorized />} />
          <Route path="/auth/verify-required" element={<VerifyRequired />} />
          
          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
          
          {/* Fundamentals */}
          <Route path="/wiki/fundamentals/pool-system" element={<ProtectedRoute><PoolSystem /></ProtectedRoute>} />
          <Route path="/wiki/fundamentals/chemistry" element={<ProtectedRoute><Chemistry /></ProtectedRoute>} />
          <Route path="/wiki/fundamentals/circulation" element={<ProtectedRoute><Circulation /></ProtectedRoute>} />
          <Route path="/wiki/fundamentals/cleaning" element={<ProtectedRoute><Cleaning /></ProtectedRoute>} />
          
          {/* Equipment */}
          <Route path="/wiki/equipment/filters" element={<ProtectedRoute><Filters /></ProtectedRoute>} />
          <Route path="/wiki/equipment/salt-systems" element={<ProtectedRoute><SaltSystems /></ProtectedRoute>} />
          <Route path="/wiki/equipment/pumps" element={<ProtectedRoute><Pumps /></ProtectedRoute>} />
          <Route path="/wiki/equipment/actuators" element={<ProtectedRoute><Actuators /></ProtectedRoute>} />
          
          {/* Water Conditions */}
          <Route path="/wiki/conditions/algae" element={<ProtectedRoute><Algae /></ProtectedRoute>} />
          <Route path="/wiki/conditions/cloudy-water" element={<ProtectedRoute><CloudyWater /></ProtectedRoute>} />
          <Route path="/wiki/conditions/green-to-clean" element={<ProtectedRoute><GreenToClean /></ProtectedRoute>} />
          
          {/* Field Procedures */}
          <Route path="/wiki/procedures/service-workflow" element={<ProtectedRoute><ServiceWorkflow /></ProtectedRoute>} />
          <Route path="/wiki/procedures/equipment-sops" element={<ProtectedRoute><EquipmentSOPs /></ProtectedRoute>} />
          <Route path="/wiki/procedures/emergency" element={<ProtectedRoute><Emergency /></ProtectedRoute>} />
          
          {/* Onboarding */}
          <Route path="/wiki/onboarding/welcome" element={<ProtectedRoute><OnboardingWelcome /></ProtectedRoute>} />
          <Route path="/wiki/onboarding/first-day" element={<ProtectedRoute><FirstDay /></ProtectedRoute>} />
          <Route path="/wiki/onboarding/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
          
          {/* Service */}
          <Route path="/wiki/service/standards" element={<ProtectedRoute><Standards /></ProtectedRoute>} />
          <Route path="/wiki/service/app-documentation" element={<ProtectedRoute><AppDocumentation /></ProtectedRoute>} />
          <Route path="/wiki/service/customer-interaction" element={<ProtectedRoute><CustomerInteraction /></ProtectedRoute>} />
          <Route path="/wiki/service/safety" element={<ProtectedRoute><Safety /></ProtectedRoute>} />
          
          {/* Compensation */}
          <Route path="/wiki/compensation/pay-incentives" element={<ProtectedRoute><PayIncentives /></ProtectedRoute>} />
          <Route path="/wiki/compensation/calculator" element={<ProtectedRoute><Calculator /></ProtectedRoute>} />
          <Route path="/wiki/compensation/faq" element={<ProtectedRoute><CompensationFAQ /></ProtectedRoute>} />
          
          {/* Policies */}
          <Route path="/wiki/policies/ic-status" element={<ProtectedRoute><ICStatus /></ProtectedRoute>} />
          <Route path="/wiki/policies/responsibilities" element={<ProtectedRoute><Responsibilities /></ProtectedRoute>} />
          <Route path="/wiki/policies/time-off-termination" element={<ProtectedRoute><TimeOffTermination /></ProtectedRoute>} />
          <Route path="/wiki/policies/uniform" element={<ProtectedRoute><Uniform /></ProtectedRoute>} />
          <Route path="/wiki/policies/conduct" element={<ProtectedRoute><Conduct /></ProtectedRoute>} />
          
          {/* Resources */}
          <Route path="/wiki/resources/route-coverage" element={<ProtectedRoute><RouteCoverage /></ProtectedRoute>} />
          <Route path="/wiki/resources/chemicals" element={<ProtectedRoute><Chemicals /></ProtectedRoute>} />
          <Route path="/wiki/resources/contacts" element={<ProtectedRoute><EmergencyContacts /></ProtectedRoute>} />
          <Route path="/wiki/resources/forms" element={<ProtectedRoute><Forms /></ProtectedRoute>} />
          <Route path="/wiki/resources/faq" element={<ProtectedRoute><ResourceFAQ /></ProtectedRoute>} />
          
          {/* Profile */}
          <Route path="/profile/settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
          <Route path="/profile/dashboard" element={<ProtectedRoute><ProfileDashboard /></ProtectedRoute>} />
          
          {/* Admin */}
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/activity-logs" element={<AdminRoute><AdminActivityLogs /></AdminRoute>} />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </TooltipProvider>
);

export default App;